/**
 * L'échéance d'une absence à rattraper — la règle de `attendanceFlagsToBeRecoveredOptions`
 * (`student/absence/RecoverFormComponent.js`), écrite une fois pour ses deux lecteurs : l'étape
 * « Quelle absence ? » (`recoverAbsenceAdapter`) et la ligne « À faire » de l'accueil
 * (`parentTodoAdapter`).
 */
import moment from "moment"

/**
 * Une ligne de `attendance_flags_to_be_recovered` (`AttendanceFlagResource`), servie par
 * `GET /api/v1/user/next-live-sessions`. `date` et `expiration_date` passent par
 * `Tz::convertToLocal` (`Y-m-d H:i`) ; `expiration_date` n'est servi que si le drapeau est
 * rattrapable, `created_by` que si la relation est chargée (elle ne l'est pas sur ce payload),
 * `description` qu'à un compte du back-office. Seuls les champs lus par les adaptateurs sont déclarés.
 */
export type ApiRecoverableAbsence = {
    id?: string | number
    date?: string | null
    created_at?: string | null
    /** Seule sa présence compte : une absence posée par l'équipe compte depuis sa création. */
    created_by?: unknown
    is_recoverable_now?: boolean
    recovery_duration_days?: number | null
    expiration_date?: string | null
    description?: string | null
}

/** `flag?.recovery_duration_days || 17` — le repli du legacy, qui est aussi celui du serveur. */
const RECOVERY_DURATION_DAYS_FALLBACK = 17

/** Le compte à rebours ne s'affiche qu'à 7 jours ou moins de l'échéance (`expiresSoon` du legacy). */
const URGENT_DAYS = 7

/** L'échéance d'une absence à rattraper : la date, les jours pleins qui restent, et si elle presse. */
export type RecoveryDeadline = {
    expirationDate: moment.Moment
    daysLeft: number
    /**
     * À 7 jours ou moins, dépassement compris : le décompte se montre, au ton d'urgence. Le serveur
     * ne sert jamais une absence échue comme rattrapable (`notExpired()`, `is_recoverable_now`) :
     * une échéance passée n'est ici qu'une lecture restée à l'écran, et elle se lit comme le
     * dernier jour.
     */
    urgent: boolean
}

export function recoveryDeadline(flag: ApiRecoverableAbsence, now: moment.Moment = moment()): RecoveryDeadline {
    const durationDays = flag.recovery_duration_days || RECOVERY_DURATION_DAYS_FALLBACK
    const isParentCreated = !flag.created_by
    const baseDate = isParentCreated && flag.date ? moment(flag.date) : moment(flag.created_at)
    const expirationDate = flag.expiration_date
        ? moment(flag.expiration_date)
        : baseDate.clone().add(durationDays, "days")
    const daysLeft = expirationDate.diff(now, "days")
    return { expirationDate, daysLeft, urgent: daysLeft <= URGENT_DAYS }
}

/**
 * Le décompte, dans les deux formes d'une même règle : `"label"` tient seul, en tête d'une option
 * (« Plus que 3 jours ») ; `"sentence"` se glisse dans une phrase (« encore 3 jours »). Le jour de
 * l'échéance, et au-delà, c'est le dernier jour.
 */
export function daysLeftLabel(daysLeft: number, form: "label" | "sentence"): string {
    if (daysLeft <= 0) return form === "label" ? "Dernier jour" : "dernier jour"
    if (form === "sentence") return `encore ${daysLeft} ${daysLeft === 1 ? "jour" : "jours"}`
    return daysLeft === 1 ? "Plus qu'un jour" : `Plus que ${daysLeft} jours`
}
